import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from './ProfileBuilder.module.scss';
import { cvUpdate, restoreAfterRefresh } from '../../actions/uploadData';
import { MOBILE_BREAKPOINT, validationMessages } from '../../utils/constants';

import AutocompleteWithBubblesNoPlusSign from '../../Components/common/Form/AutocompleteWithBubblesNoPlusSign/AutocompleteWithBubblesNoPlusSign';
import { addToFunctions, addToIndustries } from '../../Components/common/ModalWindows/helpers/helpers';
import ProfileBuilderWrapper from '../../Components/pages/profileBuilder.Wrapper/ProfileBuilderWrapper';
import { useWindowSize } from '../../utils/hooks';

export function ProfileBuilderFive() {
  const dispatch = useDispatch();
  const size = useWindowSize();
  const preferences = useSelector((state) => state.uploadData.parsedCV.JobPreferences);
  const allCvUploadStore = useSelector((state) => state.uploadData.parsedCV);
  const functionsDirections = useSelector((state) => state.constFromApi.functionsDirections);
  const industries = useSelector((state) => state.constFromApi.industries);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({ industry: false, jobs: false });
  const [t] = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const [, setRestored] = useState(false);

  const addHandler = (block, data) => {
    const flag = block === 'Industry' ? 'industry' : 'jobs';

    dispatch(
      cvUpdate({
        flag,
        value:
          block === 'Industry' ? addToIndustries(data, preferences.industry) : addToFunctions(data, preferences.jobs)
      })
    );
    setErrors({
      ...errors,
      [flag]: false
    });
  };

  const handleRefresh = () => {
    localStorage.setItem('forRestoreStore', JSON.stringify(allCvUploadStore));
  };

  const restoreData = () => {
    const data = localStorage.getItem('forRestoreStore');

    if (data) {
      const parsedData = JSON.parse(data);
      dispatch(restoreAfterRefresh(parsedData));

      localStorage.setItem('forRestoreStore', '');
    }
    setRestored(true);
  };

  const validateFields = () => {
    if (!preferences?.industry?.length || !preferences?.jobs?.length) {
      setErrors({
        industry: !preferences?.industry?.length,
        jobs: !preferences?.jobs?.length
      });
    } else {
      navigate('/dashboard/profile-builder-six');
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleRefresh);
    restoreData();
    return () => {
      window.removeEventListener('beforeunload', handleRefresh);
    };
  }, [preferences]);

  return (
    <ProfileBuilderWrapper
      titleMain={t('profile.builder.start.complete')}
      titleSecondary={t('profile.builder.jobpreferences')}
      text={t('profile.builder.mostqualified')}
      progress={89}
    >
      <div className="row py-4">
        <div className="col-10 mx-auto d-flex flex-column gap-3">
          <div className="row pt-4">
            <div className="col-12 d-flex flex-column">
              <h6 className={styles.textLeftAlign}>{t('common.forms.industry')}</h6>
              <AutocompleteWithBubblesNoPlusSign
                values={preferences?.industry || []}
                options={industries}
                addHandler={addHandler}
                block="Industry"
                byProperty="Industry"
                error={errors.industry}
                errorMsg={validationMessage.INDUSTRY_REQ}
              />
            </div>
          </div>
          <div className="row pt-4">
            <div className="col-12 d-flex flex-column">
              <h6 className={styles.textLeftAlign}>{t('common.forms.jobfunction')}</h6>
              <AutocompleteWithBubblesNoPlusSign
                values={preferences?.jobs || []}
                options={functionsDirections}
                addHandler={addHandler}
                block="Job"
                byProperty="JobFunction"
                error={errors.jobs}
                errorMsg={validationMessage.FUNCTION_REQ}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className={
          size.width > MOBILE_BREAKPOINT
            ? 'flex flex-row pt-5 justify-between'
            : 'flex flex-col-reverse justify-between'
        }
      >
        <div className={size.width > MOBILE_BREAKPOINT ? 'flex' : 'flex justify-center'}>
          <Link
            to="/dashboard/profile-builder-four"
            className="outline-button text-decoration-none p-3 px-5 mt-4"
            title=""
            data-bs-toggle="tooltip"
          >
            <span className="d-inline text-capitalize">{t('common.general.back')}</span>
          </Link>
        </div>
        <div className={size.width > MOBILE_BREAKPOINT ? 'text-start flex justify-end' : 'flex justify-center'}>
          <div
            role="presentation"
            onClick={() => (preferences.industry.length || preferences.jobs.length ? validateFields() : undefined)}
            className={`${styles.nextButton} ${
              (!preferences.industry.length || !preferences.jobs.length) && styles.disabled
            } button p-3 px-5 mt-4`}
            data-bs-toggle="tooltip"
          >
            <span className="d-inline text-capitalize">{t('common.general.next')}</span>
          </div>
        </div>
      </div>
    </ProfileBuilderWrapper>
  );
}
